import axios from 'axios';
import { BASE_URL } from './helpers';
import {
  normalizeInputAttachments,
  normalizePipelineRun,
  normalizePipelineTemplate,
  serializeCheckpointList,
  serializePipelineOutputFormat,
} from './pipelineBoundary';
import {
  AttachmentOverride,
  CastMember,
  CheckpointConfig,
  MediaAttachment,
  PipelineInputAttachment,
  PipelineOutputFormat,
  PipelineRun,
  PipelineTemplate,
  PromptTemplate,
  ReviewCorrection,
  ReviewCorrectionScope,
  ReviewLessonDistillation,
  ReviewProfileView,
  ReviewTrace,
  ReviewVerdict,
} from './structs';
import { toStringValue } from './typeHelpers';

interface StartPipelineRequest {
  template_id: string;
  initial_input: string;
  auto_mode?: boolean;
  initial_attachments?: PipelineInputAttachment[];
  music_media_id?: string | null;
  parameters?: Record<string, unknown>;
  /** Library "Run again": attribute the run to an existing idea. */
  idea_id?: string;
}

/**
 * How far a regeneration reaches:
 * - `cascade` (the backend default when omitted) resets and re-runs this
 *   checkpoint and every checkpoint after it — every later step is re-billed.
 * - `self` re-runs only this checkpoint and leaves the later steps as they are.
 */
export type RegenerateScope = 'self' | 'cascade';

/**
 * How a paused run answers an unresolved pre-generation plan review: `fix` asks
 * for one AI rewrite that follows the review's notes (then re-checks it),
 * `accept` generates from the plan as it stands.
 */
export type PlanReviewAction = 'accept' | 'fix';

export interface ContinuePipelineOptions {
  /** Sequencer failure park only: abandon the unfinished steps and advance
   *  to the next stage boundary instead of resuming them ("Skip to next
   *  checkpoint"). No-op on every other park shape. */
  skipFanout?: boolean;
  /** Unresolved plan review only. Never combined with an output override or
   *  skipFanout — the backend answers 409 when the run is not in that state. */
  planReviewAction?: PlanReviewAction;
}

interface RegenerateCheckpointRequest {
  checkpoint_index: number;
  prompt_override?: string;
  scope?: RegenerateScope;
}

interface SelectCheckpointOutputRequest {
  attachment_id: string;
}

interface AddAttachmentRequest {
  checkpoint_index: number;
  attachment: Omit<MediaAttachment, 'id' | 'created_at'>;
}

interface InjectCheckpointPromptRequest {
  text: string;
  guidance?: string;
  auto_regenerate?: boolean;
  source?: string;
}

interface InjectCheckpointPromptResponse {
  status: string;
  checkpoint_index: number;
  injection_count?: number;
  regenerated?: boolean;
}

interface CreatePipelineTemplateRequest {
  id: string;
  name: string;
  description?: string;
  output_format?: PipelineOutputFormat;
  checkpoints: CheckpointConfig[];
}

interface UpdatePipelineTemplateRequest {
  name?: string;
  description?: string;
  output_format?: PipelineOutputFormat;
  /** Run-parameter declarations. Send `{}` to clear them — an omitted/null value
   *  makes the backend KEEP the stored declarations. */
  parameters?: PipelineTemplate['parameters'];
  checkpoints?: CheckpointConfig[];
  /** Opaque node-canvas layout blob. Omitted entirely (not sent as `undefined`
   *  or `null`) means "leave the stored ui untouched" — the backend only
   *  replaces it when the key is present in the request body. */
  ui?: PipelineTemplate['ui'];
  /** Template-level review defaults. Omitted entirely means "leave the stored
   *  value untouched"; present (including `{}`) replaces it wholesale — same
   *  present/absent contract as `ui` (updatePipelineTemplate.go). */
  review_defaults?: PipelineTemplate['review_defaults'];
}

interface CreatePromptTemplateRequest {
  id: string;
  name: string;
  description?: string;
  content: string;
}

interface UpdatePromptTemplateRequest {
  name?: string;
  description?: string;
  content?: string;
}

interface PipelineOutputResponse {
  status: string;
  ready: boolean;
  output?: string;
}

const exportQualityReport = (runId: string): Promise<Blob> =>
  axios.get<Blob>(`${BASE_URL}/pipelines/${encodeURIComponent(runId)}/quality-export`, {
    responseType: 'blob',
  }).then((response) => response.data);

/** The same evidence bundle reached through a clip: its linked run plus the
 *  clip's own final render, scene media, clip QA and reel review. */
const exportClipQualityReport = (clipId: string): Promise<Blob> =>
  axios.get<Blob>(`${BASE_URL}/clips/${encodeURIComponent(clipId)}/quality-export`, {
    responseType: 'blob',
  }).then((response) => response.data);

interface StartPipelineResponse {
  run_id: string;
  status: string;
}

const serializePipelineTemplatePayload = (
  updates: UpdatePipelineTemplateRequest
): UpdatePipelineTemplateRequest => ({
  ...updates,
  output_format: serializePipelineOutputFormat(updates.output_format),
  checkpoints: updates.checkpoints ? serializeCheckpointList(updates.checkpoints) : undefined,
});

const startPipeline = (
  templateId: string,
  initialInput: string,
  options?: {
    autoMode?: boolean;
    initialAttachments?: PipelineInputAttachment[];
    musicMediaId?: string | null;
    parameters?: Record<string, unknown>;
    /** Library "Run again": attribute the run to an existing idea. */
    ideaId?: string;
  }
): Promise<StartPipelineResponse> =>
  axios
    .post(`${BASE_URL}/pipelines/start`, {
      template_id: templateId,
      initial_input: initialInput,
      auto_mode: options?.autoMode ?? true,
      initial_attachments: normalizeInputAttachments(options?.initialAttachments),
      music_media_id: toStringValue(options?.musicMediaId).trim() || undefined,
      idea_id: options?.ideaId || undefined,
      // Optional run-start parameters (e.g. { scene_count: N }); omitted when empty
      // so existing bodyless runs are unchanged and per-template defaults apply.
      parameters:
        options?.parameters && Object.keys(options.parameters).length > 0
          ? options.parameters
          : undefined,
    } as StartPipelineRequest)
    .then((res) => res.data);

const getPipeline = (pipelineId: string) =>
  axios
    .get<PipelineRun>(`${BASE_URL}/pipelines/${encodeURIComponent(pipelineId)}`)
    .then((res) => normalizePipelineRun(res.data));

const listPipelines = () =>
  axios
    .get<PipelineRun[]>(`${BASE_URL}/pipelines`)
    .then((res) => (res.data || []).map((run) => normalizePipelineRun(run)));

const continuePipeline = (
  pipelineId: string,
  outputOverride?: string,
  opts?: ContinuePipelineOptions
) =>
  axios
    .post<{ status: string }>(
      `${BASE_URL}/pipelines/${pipelineId}/continue`,
      // Send a body only when there is something to say — an edited output,
      // the skip flag or a plan-review answer — so legacy continue calls
      // remain bodyless and unchanged.
      outputOverride || opts?.skipFanout || opts?.planReviewAction
        ? {
            output_override: outputOverride || undefined,
            skip_fanout: opts?.skipFanout || undefined,
            plan_review_action: opts?.planReviewAction || undefined,
          }
        : undefined
    )
    .then((res) => res.data);

/**
 * Asks a running pipeline to stop at its next checkpoint boundary.
 *
 * The 200 means "will stop", not "has stopped": the backend never preempts an
 * in-flight provider call, so the run reports `running` with `pause_requested`
 * set until it reaches a boundary. Idempotent; a finished run answers 409.
 */
const pausePipeline = (pipelineId: string) =>
  axios
    .post<{ status: string }>(`${BASE_URL}/pipelines/${pipelineId}/pause`)
    .then((res) => res.data);

/**
 * Cancels a run that has not finished. Unlike pause, nothing resumes it: the
 * 200 means the run is cancelled (its in-flight provider call may still end).
 * A finished run answers 409.
 */
const cancelPipeline = (pipelineId: string) =>
  axios
    .post<{ status: string }>(`${BASE_URL}/pipelines/${encodeURIComponent(pipelineId)}/cancel`)
    .then((res) => res.data);

const regenerateCheckpoint = (
  pipelineId: string,
  checkpointIndex: number,
  promptOverride?: string,
  scope?: RegenerateScope
) =>
  axios
    .post<{ status: string; scope?: RegenerateScope }>(`${BASE_URL}/pipelines/${pipelineId}/regenerate`, {
      checkpoint_index: checkpointIndex,
      prompt_override: promptOverride || undefined,
      scope,
    } as RegenerateCheckpointRequest)
    .then((res) => res.data);

/**
 * Re-roll ONLY a two-frame generator's closing frame. It takes no scope: it
 * replaces one still and changes nothing downstream reads, so an operator who
 * dislikes the closing frame never has to re-roll the opening frame they
 * already approved.
 */
const regenerateClosingFrame = (
  pipelineId: string,
  checkpointIndex: number,
  promptOverride?: string
) =>
  axios
    .post<{ status: string; frame?: string }>(`${BASE_URL}/pipelines/${pipelineId}/regenerate`, {
      checkpoint_index: checkpointIndex,
      prompt_override: promptOverride || undefined,
      frame: 'closing',
    })
    .then((res) => res.data);

/**
 * Fetches the raw reviewer call log for a run. A trace read from the store
 * (source: "store") carries the full `system_prompt` / `raw_output`; a trace
 * read off the live run before the store write lands (source: "run") carries
 * only the 2 KB-capped `prompt_head` / `output_head`.
 */
const getReviewTraces = (runId: string) =>
  axios
    .get<{ traces: ReviewTrace[]; source: 'store' | 'run' }>(
      `${BASE_URL}/pipelines/${runId}/review-traces`
    )
    .then((res) => res.data);

/**
 * The reviewer's READ-ONLY picture of one template: per checkpoint, what fills
 * the judge's prompt, which judge calls run on what model, what it rules on,
 * and what it may then do. The backend derives it fresh on every request from
 * the settings cascade and the judge templates the pipeline really names, so it
 * cannot drift from what the judge sees. Nothing here is editable — the
 * capabilities themselves are written through the template and the account.
 */
const getReviewProfile = (templateId: string) =>
  axios
    .get<ReviewProfileView>(`${BASE_URL}/pipeline-templates/${templateId}/review-profile`)
    .then((res) => res.data);

/**
 * The human corrections recorded against this template's verdicts, newest
 * first — what the reviewer has been told it got wrong.
 *
 * The backend answers 200 with an EMPTY list when its store read fails, so an
 * empty result means "nothing to show", never "nothing was ever recorded".
 */
const getReviewCorrections = (templateId: string, limit = 50) =>
  axios
    .get<{ corrections: ReviewCorrection[] }>(
      `${BASE_URL}/pipeline-templates/${templateId}/review-corrections?limit=${limit}`
    )
    .then((res) => ({ corrections: res.data?.corrections || [] }));

/**
 * Turns one correction note into a lesson the judge can act on: a self-contained
 * rule, the reach it should have, and the existing lesson it repeats (if any).
 *
 * This is a JUDGE CALL — it costs credits and it can fail on its own (402 no
 * credits, 502 the judge would not answer), so it is a deliberate button, never
 * something the save path fires behind the operator's back. Saving a correction
 * without it stays legal; the backend then shows the judge the raw note.
 */
const distillReviewLesson = (
  templateId: string,
  body: {
    run_id: string;
    verdict_id: string;
    checkpoint_id: string;
    label: ReviewCorrection['label'];
    reason: string;
  }
) =>
  axios
    .post<Partial<ReviewLessonDistillation> & { reinforces_id?: string | null }>(
      `${BASE_URL}/pipeline-templates/${templateId}/review-corrections/distill`,
      body
    )
    .then(
      (res): ReviewLessonDistillation => ({
        lesson: res.data?.lesson || '',
        // A response with no scope is still usable: the template is the
        // narrowest reach that always applies.
        scope: (res.data?.scope || 'this_pipeline') as ReviewCorrectionScope,
        reinforces_id: res.data?.reinforces_id || '',
        reason: res.data?.reason || '',
        suggested_action: res.data?.suggested_action || '',
      })
    );

/**
 * Records a human override of a reviewer verdict (false pass / false fail /
 * correct). `lesson`/`scope`/`reinforces_id` come from {@link distillReviewLesson}
 * and are all optional — an undistilled correction is saved exactly as before.
 */
const correctVerdict = (
  runId: string,
  verdictId: string,
  body: {
    label: ReviewCorrection['label'];
    reason: string;
    lesson?: string;
    scope?: ReviewCorrectionScope;
    reinforces_id?: string;
  }
) =>
  axios
    .post<{ correction: ReviewCorrection; verdict: ReviewVerdict }>(
      `${BASE_URL}/pipelines/${runId}/verdicts/${verdictId}/correction`,
      body
    )
    .then((res) => res.data);

/**
 * Switches a two-frame generator's closing frame OFF (or back on). Disabled
 * means HARD-OFF: the video call gets no last-frame reference, the scene still
 * does not chain, and a later `step:N:closing` citation anchors on the scene's
 * opening frame instead. Nothing is deleted — the generations stay browsable
 * and re-enabling (or regenerating the closing frame) restores everything.
 * Addressed like every per-checkpoint frame verb: the run that OWNS the
 * generator, i.e. the CHILD run for a sequencer step.
 */
const setClosingFrameState = (
  pipelineId: string,
  checkpointIndex: number,
  disabled: boolean,
  reason?: string
) =>
  axios
    .post<{ status: string; checkpoint_index: number; disabled: boolean }>(
      `${BASE_URL}/pipelines/${pipelineId}/checkpoints/${checkpointIndex}/closing-frame-state`,
      { disabled, reason: reason || undefined }
    )
    .then((res) => res.data);

/**
 * Pins which of a checkpoint's attachments downstream steps consume when the
 * checkpoint holds several generations. An empty id clears the pin and restores
 * the default "most recent generation wins".
 */
const selectCheckpointOutput = (pipelineId: string, checkpointIndex: number, attachmentId: string) =>
  axios
    .post<{ status: string; checkpoint_index: number; selected_attachment_id: string }>(
      `${BASE_URL}/pipelines/${pipelineId}/checkpoints/${checkpointIndex}/select-output`,
      { attachment_id: attachmentId } as SelectCheckpointOutputRequest
    )
    .then((res) => res.data);

// ==================== Cast ====================
//
// The run's cast registry. Every write answers with the WHOLE cast, so a caller
// never has to merge — it replaces what it holds with what came back. Addressed
// by the ROOT run: a scene's child run has no cast of its own.

/** Every cast write answers `{ cast: [...] }`; a caller only ever wants the list. */
const castOf = (data: { cast?: CastMember[] } | undefined): CastMember[] => data?.cast || [];

/** `:name` is a path segment, and a cast name may hold spaces or a slash. */
const castPath = (pipelineId: string, name: string) =>
  `${BASE_URL}/pipelines/${pipelineId}/cast/${encodeURIComponent(name)}`;

/** What one cast-member edit may change. Anything the body omits is untouched. */
export interface CastMemberUpdate {
  anchors?: string[];
  aliases?: string[];
  media_id?: string;
  locked?: boolean;
}

/**
 * Edits one member. The backend locks whatever it writes (`locked: true`,
 * `source: "user"`) — a human's picture and anchors outrank any later plan —
 * so the answer is the truth about the member, not the request echoed back.
 */
const updateCastMember = (pipelineId: string, name: string, updates: CastMemberUpdate) =>
  axios
    .patch<{ cast?: CastMember[] }>(castPath(pipelineId, name), updates)
    .then((res) => castOf(res.data));

const addCastMember = (
  pipelineId: string,
  member: { name: string; media_id: string; anchors?: string[] }
) =>
  axios
    .post<{ cast?: CastMember[] }>(`${BASE_URL}/pipelines/${pipelineId}/cast`, member)
    .then((res) => castOf(res.data));

const removeCastMember = (pipelineId: string, name: string) =>
  axios.delete<{ cast?: CastMember[] }>(castPath(pipelineId, name)).then((res) => castOf(res.data));

/**
 * Copies the cast of the newest completed run of the same template — the same
 * characters, across runs, without re-uploading a picture. Every member lands
 * locked. Answers 404 when this pipeline has no earlier run with a cast.
 */
const loadPreviousCast = (pipelineId: string) =>
  axios
    .post<{ cast?: CastMember[] }>(`${BASE_URL}/pipelines/${pipelineId}/cast/load-previous`)
    .then((res) => castOf(res.data));

/** What one attachment-override write asks for. See `setAttachmentOverride`. */
export interface AttachmentOverrideRequest {
  /** `checkpoint:<id>` / `checkpoint:<id>:<attachment_id>` specs to feed in. */
  add?: string[];
  /** Attachment ids, media ids or `checkpoint:…` specs to stop feeding. */
  drop?: string[];
  reason?: string;
  /**
   * `true` stores exactly this add/drop set instead of merging into whatever
   * override the checkpoint already carries — the full-state write the rewire
   * modal makes (which is what lets a previously dropped reference be put
   * back). `replace: true` with empty lists clears the override entirely.
   */
  replace?: boolean;
  /**
   * Which frame of a two-frame generator this rewire is about — the same
   * vocabulary `regenerate`'s `frame` and `?frame=closing` speak. `closing`
   * drops only reach the closing frame's own copies, `opening` drops only the
   * opening frame's, and each frame's full-state write leaves the other
   * standing.
   *
   * OMITTED is the unqualified rewire the one-bucket surface has always sent:
   * one flat list whose drops reach both frames. It is not the same as
   * `opening`, and it is deliberately what the distributor's scene card keeps
   * sending.
   */
  frame?: 'opening' | 'closing';
}

export interface AttachmentOverrideResponse {
  status: string;
  checkpoint_index: number;
  checkpoint_id: string;
  /** The EFFECTIVE override now stored for the checkpoint. */
  override: AttachmentOverride;
}

/**
 * Rewires which reference images one checkpoint's generator is fed. The run
 * addressed must be the run that OWNS the checkpoint — for a sequencer step or
 * a distributor scene that is the CHILD run, not the parent.
 */
const setAttachmentOverride = (
  pipelineId: string,
  checkpointIndex: number,
  request: AttachmentOverrideRequest
): Promise<AttachmentOverrideResponse> =>
  axios
    .post<AttachmentOverrideResponse>(
      `${BASE_URL}/pipelines/${pipelineId}/checkpoints/${checkpointIndex}/attachment-override`,
      {
        add: request.add || [],
        drop: request.drop || [],
        reason: toStringValue(request.reason).trim() || undefined,
        replace: request.replace ?? true,
        // Omitted, never empty-stringed: an absent frame is the unqualified
        // rewire, which is a different write from an opening-frame one.
        ...(request.frame ? { frame: request.frame } : {}),
      }
    )
    .then((res) => res.data);

const addAttachment = (
  pipelineId: string,
  checkpointIndex: number,
  attachment: Omit<MediaAttachment, 'id' | 'created_at'>
) =>
  axios
    .post<{ status: string }>(`${BASE_URL}/pipelines/${pipelineId}/attachments`, {
      checkpoint_index: checkpointIndex,
      attachment,
    } as AddAttachmentRequest)
    .then((res) => res.data);

const injectCheckpointPrompt = (
  pipelineId: string,
  checkpointIndex: number,
  text: string,
  options?: {
    autoRegenerate?: boolean;
    source?: string;
  }
) =>
  axios
    .post<InjectCheckpointPromptResponse>(
      `${BASE_URL}/pipelines/${pipelineId}/checkpoints/${checkpointIndex}/inject`,
      {
        text,
        guidance: text,
        auto_regenerate: options?.autoRegenerate,
        source: toStringValue(options?.source).trim() || undefined,
      } as InjectCheckpointPromptRequest
    )
    .then((res) => res.data);

/** `frame: 'closing'` returns the planner's closing-frame prompt, which lives in
 *  the scene plan rather than in a prompt template. */
const getCheckpointPrompt = (
  pipelineId: string,
  checkpointIndex: number,
  frame?: 'closing'
): Promise<{ resolved_prompt: string }> =>
  axios
    .get(
      `${BASE_URL}/pipelines/${pipelineId}/checkpoints/${checkpointIndex}/prompt`,
      // Passed only when a frame is asked for, so the call shape for the
      // opening frame stays byte-identical to what it always was.
      ...(frame ? [{ params: { frame } }] : [])
    )
    .then((res) => res.data);

const deletePipeline = (pipelineId: string) =>
  axios
    .delete<{ status: string }>(`${BASE_URL}/pipelines/${pipelineId}`)
    .then((res) => res.data);

// Retires a finished run without deleting it: the run list stops returning it,
// but GET /pipelines/:id and the quality exports still can. Idempotent; 409 for
// a run that has not finished, 400 for a child run.
const archivePipeline = (pipelineId: string) =>
  axios
    .post<{ status: string }>(`${BASE_URL}/pipelines/${pipelineId}/archive`)
    .then((res) => res.data);

// Bulk-purges every pipeline run (admin-JWT gated server-side). Used to offload
// legacy runs that have accumulated over time; unlike deletePipeline this is
// not scoped to a single run and cannot be undone.
const purgeAllPipelineRuns = (): Promise<{ deleted: number }> =>
  axios
    .delete<{ deleted: number }>(`${BASE_URL}/admin/pipelines/runs`)
    .then((res) => res.data);

/** Which steps a per-step regeneration re-runs: the step itself first, then
 *  every later step that transitively references it, ascending. */
export interface SequencerDirtyResponse {
  dirty_orders: number[];
}

/**
 * Server-side preview of a sequencer step's dirty set, so the confirm warning
 * can state exactly what the backend will re-run. The UI also computes this
 * client-side from the refs graph, and prefers this answer when it arrives.
 */
const getSequencerStepDirty = (
  pipelineId: string,
  checkpointIndex: number,
  order: number
): Promise<SequencerDirtyResponse> =>
  axios
    .get<SequencerDirtyResponse>(
      `${BASE_URL}/pipelines/${pipelineId}/checkpoints/${checkpointIndex}/steps/${order}/dirty`
    )
    .then((res) => res.data);

export interface RegenerateSequencerStepOptions {
  promptOverride?: string;
  /**
   * `false` re-runs SOLELY the addressed step, leaving the steps that reference
   * it untouched. Omitted (or `true`) keeps the legacy behaviour: the step plus
   * its whole dirty set.
   */
  cascade?: boolean;
  /**
   * A corrective note APPENDED to the step's own prompt for this regeneration
   * only — "Mara must already hold the lantern". Unlike `promptOverride`, which
   * replaces the prompt wholesale, the step still re-runs from its plan; the
   * note is what the reviewer found wrong with what it produced.
   */
  guidance?: string;
}

/**
 * Re-runs one sequencer step and — unless `cascade: false` — its dirty set. The
 * backend replaces those child runs in place (same positions in
 * child_pipeline_ids), re-runs them serially, then refreshes the sequencer's
 * attachments and any completed downstream connector.
 */
const regenerateSequencerStep = (
  pipelineId: string,
  checkpointIndex: number,
  order: number,
  options?: RegenerateSequencerStepOptions
): Promise<SequencerDirtyResponse> => {
  const body: { prompt_override?: string; cascade?: boolean; guidance?: string } = {};
  if (options?.promptOverride) body.prompt_override = options.promptOverride;
  if (typeof options?.cascade === 'boolean') body.cascade = options.cascade;
  if (options?.guidance?.trim()) body.guidance = options.guidance.trim();
  return axios
    .post<SequencerDirtyResponse>(
      `${BASE_URL}/pipelines/${pipelineId}/checkpoints/${checkpointIndex}/steps/${order}/regenerate`,
      body
    )
    .then((res) => res.data);
};

const removeChildScene = (parentPipelineId: string, childPipelineId: string) =>
  axios
    .delete<{ status: string }>(`${BASE_URL}/pipelines/${parentPipelineId}/child-scenes/${childPipelineId}`)
    .then((res) => res.data);

const createPipelineTemplate = (
  id: string,
  name: string,
  checkpoints: CheckpointConfig[],
  description?: string,
  outputFormat?: PipelineOutputFormat
) =>
  axios
    .post<PipelineTemplate>(`${BASE_URL}/pipeline-templates`, {
      id,
      name,
      description,
      output_format: serializePipelineOutputFormat(outputFormat),
      checkpoints: serializeCheckpointList(checkpoints),
    } as CreatePipelineTemplateRequest)
    .then((res) => normalizePipelineTemplate(res.data));

const getPipelineTemplate = (templateId: string) =>
  axios
    .get<PipelineTemplate>(`${BASE_URL}/pipeline-templates/${templateId}`)
    .then((res) => normalizePipelineTemplate(res.data));

const listPipelineTemplates = () =>
  axios
    .get<PipelineTemplate[]>(`${BASE_URL}/pipeline-templates`)
    .then((res) => (res.data || []).map((template) => normalizePipelineTemplate(template)));

/**
 * A saved template plus the backend's non-blocking findings (`warnings`, e.g. an
 * inert declared-but-unreferenced parameter). The save response embeds them
 * alongside the template; they used to be dropped by normalization.
 */
export type SavedPipelineTemplate = PipelineTemplate & { warnings?: string[] };

const readSaveWarnings = (data: unknown): string[] | undefined => {
  const warnings = (data as { warnings?: unknown } | null)?.warnings;
  return Array.isArray(warnings) ? warnings.filter((w): w is string => typeof w === 'string') : undefined;
};

const updatePipelineTemplate = (
  templateId: string,
  updates: UpdatePipelineTemplateRequest
): Promise<SavedPipelineTemplate> =>
  axios
    .put<PipelineTemplate>(
      `${BASE_URL}/pipeline-templates/${templateId}`,
      serializePipelineTemplatePayload(updates)
    )
    .then((res) => ({ ...normalizePipelineTemplate(res.data), warnings: readSaveWarnings(res.data) }));

const deletePipelineTemplate = (templateId: string) =>
  axios
    .delete<{ status: string }>(`${BASE_URL}/pipeline-templates/${templateId}`)
    .then((res) => res.data);

const clonePipelineTemplate = (templateId: string) =>
  axios
    .post<PipelineTemplate>(`${BASE_URL}/pipeline-templates/${templateId}/clone`)
    .then((res) => normalizePipelineTemplate(res.data));

export interface PipelineTemplateBundle {
  pipeline: PipelineTemplate;
  prompt_templates: PromptTemplate[];
}

const exportPipelineTemplate = (templateId: string): Promise<PipelineTemplateBundle> =>
  axios
    .get<{ pipeline: unknown; prompt_templates?: unknown }>(
      `${BASE_URL}/pipeline-templates/${templateId}/export`
    )
    .then((res) => ({
      pipeline: normalizePipelineTemplate(res.data?.pipeline),
      prompt_templates: Array.isArray(res.data?.prompt_templates)
        ? (res.data.prompt_templates as PromptTemplate[])
        : [],
    }));

const importPipelineTemplate = (bundle: PipelineTemplateBundle): Promise<PipelineTemplate> =>
  axios
    .post<PipelineTemplate>(`${BASE_URL}/pipeline-templates/import`, bundle)
    .then((res) => normalizePipelineTemplate(res.data));

interface SyncLocalResponse {
  pipelines_synced: number;
  prompts_synced: number;
  errors?: string[];
}

const syncLocalToRemote = (): Promise<SyncLocalResponse> =>
  axios
    .post<SyncLocalResponse>(`${BASE_URL}/pipeline-templates/sync-local`)
    .then((res) => res.data);

const getPipelineOutput = (pipelineId: string) =>
  axios
    .get<PipelineOutputResponse>(`${BASE_URL}/pipelines/${pipelineId}/output`)
    .then((res) => res.data);

/**
 * Stamps a prompt-template write with the pipeline it belongs to.
 *
 * A craft doctrine is owned per pipeline: given the pipeline, the server forks
 * the template when ANY OTHER pipeline references it, instead of counting
 * references. Omitted when the caller has no pipeline (or is not writing on a
 * pipeline's behalf) — the field is then absent from the body entirely, not
 * present-and-undefined, so the server keeps its caller-agnostic rule and every
 * other caller of these two helpers behaves exactly as before.
 *
 * Deliberately NOT part of `UpdatePromptTemplateRequest`: it is write context,
 * not a field of the template, and must never read as something stored.
 */
const withPipelineContext = <T extends object>(body: T, pipelineTemplateId?: string): T => {
  const id = pipelineTemplateId?.trim();
  return id ? { ...body, pipeline_template_id: id } : body;
};

const createPromptTemplate = (
  id: string,
  name: string,
  content: string,
  description?: string,
  pipelineTemplateId?: string
) =>
  axios
    .post<PromptTemplate>(
      `${BASE_URL}/prompt-templates`,
      withPipelineContext(
        {
          id,
          name,
          description,
          content,
        } as CreatePromptTemplateRequest,
        pipelineTemplateId
      )
    )
    .then((res) => res.data);

const getPromptTemplate = (templateId: string) =>
  axios
    .get<PromptTemplate>(`${BASE_URL}/prompt-templates/${templateId}`)
    .then((res) => res.data);

const listPromptTemplates = () =>
  axios
    .get<PromptTemplate[]>(`${BASE_URL}/prompt-templates`)
    .then((res) => res.data || []);

const updatePromptTemplate = (
  templateId: string,
  updates: UpdatePromptTemplateRequest,
  pipelineTemplateId?: string
) =>
  axios
    .put<PromptTemplate>(
      `${BASE_URL}/prompt-templates/${templateId}`,
      withPipelineContext(updates, pipelineTemplateId)
    )
    .then((res) => res.data);

const deletePromptTemplate = (templateId: string) =>
  axios
    .delete<{ status: string }>(`${BASE_URL}/prompt-templates/${templateId}`)
    .then((res) => res.data);

/** Copies a global (pre-defined) prompt template into the caller's own collection. */
const clonePromptTemplate = (templateId: string) =>
  axios
    .post<PromptTemplate>(`${BASE_URL}/prompt-templates/${templateId}/clone`)
    .then((res) => res.data);

const PipelineAPI = {
  exportQualityReport,
  exportClipQualityReport,
  startPipeline,
  getPipeline,
  listPipelines,
  continuePipeline,
  pausePipeline,
  cancelPipeline,
  regenerateCheckpoint,
  selectCheckpointOutput,
  regenerateClosingFrame,
  getReviewTraces,
  getReviewProfile,
  getReviewCorrections,
  distillReviewLesson,
  correctVerdict,
  setClosingFrameState,
  setAttachmentOverride,
  updateCastMember,
  addCastMember,
  removeCastMember,
  loadPreviousCast,
  getCheckpointPrompt,
  addAttachment,
  injectCheckpointPrompt,
  deletePipeline,
  archivePipeline,
  purgeAllPipelineRuns,
  removeChildScene,
  getSequencerStepDirty,
  regenerateSequencerStep,
  createPipelineTemplate,
  getPipelineTemplate,
  listPipelineTemplates,
  updatePipelineTemplate,
  deletePipelineTemplate,
  clonePipelineTemplate,
  exportPipelineTemplate,
  importPipelineTemplate,
  syncLocalToRemote,
  createPromptTemplate,
  getPromptTemplate,
  listPromptTemplates,
  updatePromptTemplate,
  deletePromptTemplate,
  clonePromptTemplate,
  getPipelineOutput,
};

export default PipelineAPI;
